import { useId, useState } from "react";
import { useTranslations } from "next-intl";
import { COURSE_SLUG_MAX, COURSE_TITLE_MAX } from "@lms/backend/lib/courses/create-course-form";
import { ApiError, apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

export type OverviewCourse = {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  thumbnailUrl?: string | null;
  courseType: string;
  status: string;
  metadata: Record<string, unknown> | null;
};

type Props = {
  courseId: string;
  course: OverviewCourse;
  onSaved: (course: OverviewCourse) => Promise<void> | void;
};

const inputClass =
  "mt-1 w-full rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]";

/**
 * F1 (CRS-3): the builder's editable Overview — title, slug, description, thumbnail URL and, for
 * a retreat, its location. Changing a published course's slug moves its public and learn URLs,
 * so that is said inline and confirmed before saving; a taken slug is shown under the field.
 */
export function CourseOverviewForm({ courseId, course, onSaved }: Props) {
  const t = useTranslations("instructor");
  const tRoot = useTranslations();
  const ids = { slugHint: useId(), slugError: useId(), thumbHint: useId() };
  const [title, setTitle] = useState(course.title);
  const [slug, setSlug] = useState(course.slug);
  const [description, setDescription] = useState(course.description ?? "");
  const [thumbnailUrl, setThumbnailUrl] = useState(course.thumbnailUrl ?? "");
  const [location, setLocation] = useState(
    typeof course.metadata?.location === "string" ? course.metadata.location : "",
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);

  const isRetreat = course.courseType === "retreat";
  const slugChanging = slug.trim() !== course.slug;
  const warnSlug = slugChanging && course.status === "published";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSlugError(null);
    setSaved(false);
    if (warnSlug && !window.confirm(t("slugChangeConfirm"))) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() ? description.trim() : null,
          thumbnailUrl: thumbnailUrl.trim() ? thumbnailUrl.trim() : null,
          slug: slug.trim(),
          ...(isRetreat ? { metadata: { location: location.trim() } } : {}),
        }),
      });
      if (!res.ok) {
        const err = await readApiErrorResponse(res);
        if (err instanceof ApiError && err.code === "SLUG_EXISTS") {
          setSlugError(t("slugTaken"));
          return;
        }
        setError(
          apiErrorText(err, tRoot, "instructor.overviewSaveFailed", {
            namespace: "instructor.errors",
          }),
        );
        return;
      }
      const json = (await res.json()) as { data: { course: OverviewCourse } };
      setSaved(true);
      await onSaved(json.data.course);
    } catch (err) {
      setError(
        apiErrorText(err, tRoot, "instructor.overviewSaveFailed", {
          namespace: "instructor.errors",
        }),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="grid gap-4 rounded-lg border border-[var(--border)] p-4 text-sm"
      data-testid="course-overview-form"
    >
      <label className="block font-medium">
        {t("titleLabel")}
        <input
          data-testid="overview-title"
          className={inputClass}
          value={title}
          maxLength={COURSE_TITLE_MAX}
          required
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>
      <label className="block font-medium">
        {t("slugLabel")}
        <input
          data-testid="overview-slug"
          className={inputClass}
          value={slug}
          maxLength={COURSE_SLUG_MAX}
          required
          pattern="[a-z0-9]+(-[a-z0-9]+)*"
          aria-invalid={slugError ? true : undefined}
          aria-describedby={
            [warnSlug ? ids.slugHint : null, slugError ? ids.slugError : null]
              .filter(Boolean)
              .join(" ") || undefined
          }
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
        />
      </label>
      {warnSlug ? (
        <p
          id={ids.slugHint}
          className="-mt-2 text-sm text-[var(--warning)]"
          data-testid="slug-change-warning"
        >
          {t("slugChangeWarning")}
        </p>
      ) : null}
      {slugError ? (
        <p
          id={ids.slugError}
          className="-mt-2 text-sm text-red-600"
          role="alert"
          data-testid="overview-slug-error"
        >
          {slugError}
        </p>
      ) : null}
      <label className="block font-medium">
        {t("descriptionLabel")}
        <textarea
          data-testid="overview-description"
          className={`${inputClass} min-h-24`}
          value={description}
          maxLength={10_000}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <label className="block font-medium">
        {t("thumbnailUrlLabel")}
        <input
          data-testid="overview-thumbnail-url"
          type="url"
          inputMode="url"
          className={inputClass}
          value={thumbnailUrl}
          placeholder="https://"
          aria-describedby={ids.thumbHint}
          onChange={(e) => setThumbnailUrl(e.target.value)}
        />
      </label>
      <p id={ids.thumbHint} className="-mt-2 text-xs text-[var(--text-muted)]">
        {t("thumbnailUrlHint")}
      </p>
      {isRetreat ? (
        <label className="block font-medium">
          {t("locationLabel")}
          <input
            data-testid="overview-location"
            className={inputClass}
            value={location}
            maxLength={500}
            onChange={(e) => setLocation(e.target.value)}
          />
        </label>
      ) : null}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          data-testid="overview-save"
          disabled={saving}
          className="rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)] disabled:opacity-60"
        >
          {saving ? t("saving") : t("overviewSave")}
        </button>
        {saved ? (
          <span
            className="text-sm text-green-700 dark:text-green-400"
            role="status"
            data-testid="overview-saved"
          >
            {t("overviewSaved")}
          </span>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-red-600" data-testid="overview-error">
          {error}
        </p>
      ) : null}
    </form>
  );
}
